import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TeButton,
  TeButtonMinimal,
  TeContainer,
  TeContainerSlim,
  TeContextMenu,
  TeContextMenuControl,
  TeContextMenuItem,
  TeDivider,
  TeIcon,
  TeText,
} from '@topcon/tuicl-react';
import { PlaceholderContainer } from '@topcon/topcon-utils';
import { styled } from 'styled-components';

import { Section } from '../../../utils/styling';
import Header from '../../../components/Header';
import AddEditIdentityProviderModal from './AddEditIdentityProviderModal';
import DeleteIdentityProviderModal from './DeleteIdentityProviderModal';
import { useIdentityProviders } from './identityProviders.queries';
import { useDomains } from '../DomainRegistration/domainRegistration.queries';
import NoVerifiedDomainsModal from './NoVerifiedDomainsModal';
import { copyToClipboard } from '../../../utils/utils';
import { border, common } from '../../../providers/theme/theme.index';
import { IdentityProviderBody } from '../../../api/generated/pap';

const IdentityProviders = () => {
  const { t } = useTranslation('settings');
  const [selectedIdentityProvider, setSelectedIdentityProvider] =
    useState<IdentityProviderBody | null>(null);
  const [identityProviderModalOpen, setIdentityProviderModalOpen] = useState(false);
  const [deleteIdentityProviderModalOpen, setDeleteIdentityProviderModalOpen] = useState(false);
  const [noVerifiedDomainsModalOpen, setNoVerifiedDomainsModalOpen] = useState(false);
  const { data: domainsResponse, isFetched: loadedDomains } = useDomains();
  const domains = domainsResponse?.items || [];
  const {
    data: identityProvidersResponse,
    isFetched: loaded,
    refetch: refetchIdentityProviders,
  } = useIdentityProviders();
  const { items: identityProviders } = identityProvidersResponse ?? { items: [] };

  const openIdentityProviderAddEditModal = (provider: IdentityProviderBody | null) => {
    setSelectedIdentityProvider(provider);
    if (!provider && !domains.some((domain) => domain.verified)) {
      setNoVerifiedDomainsModalOpen(true);
    } else {
      setIdentityProviderModalOpen(true);
    }
  };

  const handleAddIdentityProviderModalClose = (shouldUpdate: boolean) => {
    setIdentityProviderModalOpen(false);
    if (shouldUpdate) refetchIdentityProviders();
  };

  const handleDeleteIdentityProviderModalClose = (shouldUpdate: boolean) => {
    setDeleteIdentityProviderModalOpen(false);
    if (shouldUpdate) refetchIdentityProviders();
  };

  const openIdentityProviderDeleteModal = async (identityProvider: IdentityProviderBody) => {
    setSelectedIdentityProvider(identityProvider);
    setDeleteIdentityProviderModalOpen(true);
  };

  const renderEmptyState = () => {
    return (
      <PlaceholderContainer
        icon="portrait"
        text={t('identityProviders.noIdentityProvidersYet')}
        secondaryText={t('clickButtonToAddOne')}
      >
        <TeButton
          text={t('identityProviders.addIdentityProvider')}
          size="s"
          teclick={() => openIdentityProviderAddEditModal(null)}
        />
      </PlaceholderContainer>
    );
  };

  const renderLoader = () => {
    return (
      <PlaceholderContainer hasLoader text={t('identityProviders.loadingIdentityProviders')} />
    );
  };

  const renderNoContent = () => {
    return (
      <TeContainerSlim
        backgroundColor="background.secondary"
        borderRadius={4}
        column
        fill
        justifyContent="center"
        alignItems="center"
      >
        {!loaded ? renderLoader() : renderEmptyState()}
      </TeContainerSlim>
    );
  };

  const renderIdentityProviders = () => {
    return identityProviders.map((provider: IdentityProviderBody) => (
      <TeContainerSlim
        key={provider.id}
        column
        grow
        backgroundColor="background.secondary"
        borderRadius={4}
      >
        <TeContainer fill justifyContent="space-between" alignItems="center">
          <TeContainerSlim fill gap="s" alignItems="center">
            <TeIcon name="portrait" size="l" />
            <TeText size={16} weight={700}>
              {provider.name}
            </TeText>
          </TeContainerSlim>
          <TeContextMenuControl contextId={`provider${provider.id}`}>
            <TeButton icon="more_horiz" size="s" variant="neutral" />
          </TeContextMenuControl>
          <TeContextMenu contextId={`provider${provider.id}`} placement="bottomLeft" size="s">
            <TeContextMenuItem
              text={t('edit')}
              teclick={() => openIdentityProviderAddEditModal(provider)}
            />
            <TeContextMenuItem
              text={t('delete')}
              teclick={() => {
                openIdentityProviderDeleteModal(provider);
              }}
              textColor="text.error.primary"
            />
          </TeContextMenu>
        </TeContainer>
        <TeDivider dividerColor="border.secondary" />
        <TeContainerSlim padding="m" fill>
          <TeContainerSlim column>
            <TeContainerSlim
              fill
              alignItems="center"
              height={34}
              padding={[0, 0, 0, 'm']}
              backgroundColor="background.tertiary"
            >
              <TeText size={12}>{t('identityProviders.realm')}</TeText>
            </TeContainerSlim>
            {provider.OIDCConfig?.issuerURL ? (
              <TeContainerSlim fill alignItems="center" height={34} padding={[0, 0, 0, 'm']}>
                <TeText size={12}>{t('identityProviders.issuerUrl')}</TeText>
              </TeContainerSlim>
            ) : null}
            {provider.OIDCConfig?.authURL ? (
              <TeContainerSlim
                fill
                alignItems="center"
                height={34}
                padding={[0, 0, 0, 'm']}
                backgroundColor="background.tertiary"
              >
                <TeText size={12}>{t('identityProviders.authUrl')}</TeText>
              </TeContainerSlim>
            ) : null}
            {provider.OIDCConfig?.tokenURL ? (
              <TeContainerSlim fill alignItems="center" height={34} padding={[0, 0, 0, 'm']}>
                <TeText size={12}>{t('identityProviders.tokenUrl')}</TeText>
              </TeContainerSlim>
            ) : null}
            {provider.OIDCConfig?.userInfoURL ? (
              <TeContainerSlim
                fill
                alignItems="center"
                height={34}
                padding={[0, 0, 0, 'm']}
                backgroundColor="background.tertiary"
              >
                <TeText size={12}>{t('identityProviders.userInfoUrl')}</TeText>
              </TeContainerSlim>
            ) : null}
            {provider.OIDCConfig?.jwksURL ? (
              <TeContainerSlim fill alignItems="center" height={34} padding={[0, 0, 0, 'm']}>
                <TeText size={12}>{t('identityProviders.jwksUrl')}</TeText>
              </TeContainerSlim>
            ) : null}
            {provider.OIDCConfig?.clientId ? (
              <TeContainerSlim
                fill
                alignItems="center"
                height={34}
                padding={[0, 0, 0, 'm']}
                backgroundColor="background.tertiary"
              >
                <TeText size={12}>{t('identityProviders.clientId')}</TeText>
              </TeContainerSlim>
            ) : null}
            {provider.metaDataURL ? (
              <TeContainerSlim
                fill
                alignItems="center"
                height={34}
                padding={[0, 0, 0, 'm']}
                backgroundColor="background.tertiary"
              >
                <TeText size={12}>{t('identityProviders.metadataURL')}</TeText>
              </TeContainerSlim>
            ) : null}
          </TeContainerSlim>

          <TeContainerSlim column grow>
            <TeContainerSlim
              fill
              alignItems="center"
              padding={['xs', 'm']}
              backgroundColor="background.tertiary"
            >
              <TeText weight={500}>{provider.realm}</TeText>
            </TeContainerSlim>
            {provider.OIDCConfig?.issuerURL ? (
              <TeContainerSlim fill alignItems="center" padding={['xs', 'm']}>
                <TeText weight={500}>{provider.OIDCConfig?.issuerURL}</TeText>
              </TeContainerSlim>
            ) : null}
            {provider.OIDCConfig?.authURL ? (
              <TeContainerSlim
                fill
                alignItems="center"
                padding={['xs', 'm']}
                backgroundColor="background.tertiary"
              >
                <TeText weight={500}>{provider.OIDCConfig?.authURL}</TeText>
              </TeContainerSlim>
            ) : null}
            {provider.OIDCConfig?.tokenURL ? (
              <TeContainerSlim fill alignItems="center" padding={['xs', 'm']}>
                <TeText weight={500}>{provider.OIDCConfig?.tokenURL}</TeText>
              </TeContainerSlim>
            ) : null}
            {provider.OIDCConfig?.userInfoURL ? (
              <TeContainerSlim
                fill
                alignItems="center"
                padding={['xs', 'm']}
                backgroundColor="background.tertiary"
              >
                <TeText weight={500}>{provider.OIDCConfig?.userInfoURL}</TeText>
              </TeContainerSlim>
            ) : null}
            {provider.OIDCConfig?.jwksURL ? (
              <TeContainerSlim fill alignItems="center" padding={['xs', 'm']}>
                <TeText weight={500}>{provider.OIDCConfig?.jwksURL}</TeText>
              </TeContainerSlim>
            ) : null}
            {provider.OIDCConfig?.clientId ? (
              <TeContainerSlim
                fill
                alignItems="center"
                padding={['xs', 'm']}
                backgroundColor="background.tertiary"
              >
                <TeText weight={500}>{provider.OIDCConfig?.clientId}</TeText>
              </TeContainerSlim>
            ) : null}
            {provider.metaDataURL ? (
              <TeContainerSlim
                fill
                alignItems="center"
                padding={['xs', 'm']}
                backgroundColor="background.tertiary"
              >
                <TeText weight={500}>{provider.metaDataURL}</TeText>
              </TeContainerSlim>
            ) : null}
          </TeContainerSlim>
        </TeContainerSlim>

        {provider.metaDataURL ? (
          <>
            <TeDivider dividerColor="border.secondary" />
            <TeContainer padding="m">
              {provider.SAMLConfig ? (
                <TeButton
                  text={t('identityProviders.downloadMetadata')}
                  size="s"
                  icon="download"
                  variant="neutral"
                  teclick={() => window.open(provider.metaDataURL, '_blank')}
                />
              ) : (
                <TeButton
                  text={t('identityProviders.copyCurlCommand')}
                  size="s"
                  icon="content_copy"
                  variant="neutral"
                  teclick={() => copyToClipboard(`curl ${provider.redirectURL}`)}
                />
              )}
            </TeContainer>
          </>
        ) : null}
        {provider.redirectURL ? (
          <>
            <TeDivider dividerColor="border.secondary" />
            <TeContainer padding="m">
              <TeContainerSlim
                padding={[0, 's']}
                height={32}
                borderRadius={4}
                gap="xs"
                backgroundColor="background.primary"
                alignItems="center"
                borderColor="border.secondary"
              >
                <TeContainerSlim gap="xs2" alignItems="center">
                  <TeIcon name="info" />
                  <TeText size={12}>{t('identityProviders.callbackURL')}</TeText>
                </TeContainerSlim>
                <TeText weight={700}>{provider.redirectURL}</TeText>
                <ButtonDivider />
                <TeButtonMinimal
                  size="xs"
                  icon="content_copy"
                  text={t('copy')}
                  teclick={() => copyToClipboard(provider.redirectURL ?? '')}
                />
              </TeContainerSlim>
            </TeContainer>
          </>
        ) : null}
      </TeContainerSlim>
    ));
  };

  return (
    <TeContainer column grow gap="xs" padding="xs" overflow="auto" alignItems="center">
      <Header text={t('identityProviders.title')}>
        <TeButton
          text={t('identityProviders.addIdentityProvider')}
          size="s"
          icon="add"
          teclick={() => openIdentityProviderAddEditModal(null)}
          disabled={!loaded}
        />
      </Header>

      <Section>
        <TeContainer column fill gap="s">
          {identityProviders.length ? renderIdentityProviders() : renderNoContent()}
        </TeContainer>
      </Section>

      {identityProviderModalOpen && (
        <AddEditIdentityProviderModal
          open={identityProviderModalOpen}
          identityProvider={selectedIdentityProvider}
          onClose={handleAddIdentityProviderModalClose}
          domains={domains}
          loadedDomains={loadedDomains}
        />
      )}

      {selectedIdentityProvider && (
        <DeleteIdentityProviderModal
          open={deleteIdentityProviderModalOpen}
          onClose={handleDeleteIdentityProviderModalClose}
          identityProvider={selectedIdentityProvider}
        />
      )}

      <NoVerifiedDomainsModal
        open={noVerifiedDomainsModalOpen}
        onClose={() => {
          setNoVerifiedDomainsModalOpen(false);
        }}
      />
    </TeContainer>
  );
};

export default IdentityProviders;

const ButtonDivider = styled.div`
  height: 100%;
  border-right: ${border.secondary} 1px solid;
  padding-left: ${common.padding.xs2};
`;
